import React, { useState, useEffect } from 'react';
import { Select, Spin, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const { Option } = Select;

/**
 * 资产搜索选择组件
 * 支持搜索和选择指定类型的资产
 */
const AssetSearchSelect = ({ 
  assetTypeId, 
  value, 
  onChange, 
  placeholder = "请选择资产",
  disabled = false,
  style = { width: '100%' }
}) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);

  // 搜索资产
  const searchAssets = async (searchText = '') => {
    if (!assetTypeId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/v2/assets?type_id=${assetTypeId}&search=${encodeURIComponent(searchText)}&limit=50`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setAssets(data.data.assets || []);
      } else {
        message.error(data.message || '搜索资产失败');
        setAssets([]);
      }
    } catch (error) {
      console.error('搜索资产失败:', error);
      message.error('搜索资产失败');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  // 根据资产ID获取资产详情
  const fetchAssetById = async (assetId) => {
    if (!assetId) return null;
    
    try {
      const response = await fetch(`/api/v2/assets/${assetId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        return data.data;
      }
    } catch (error) {
      console.error('获取资产详情失败:', error);
    }
    
    return null;
  };

  // 初始加载
  useEffect(() => {
    if (assetTypeId) {
      searchAssets();
    }
  }, [assetTypeId]);

  // 处理初始值
  useEffect(() => {
    if (value && !selectedAsset) {
      fetchAssetById(value).then(asset => {
        if (asset) {
          setSelectedAsset(asset);
          // 将选中的资产添加到列表中，确保能正确显示
          setAssets(prevAssets => {
            const exists = prevAssets.find(a => a.id === asset.id);
            if (!exists) {
              return [asset, ...prevAssets];
            }
            return prevAssets;
          });
        }
      });
    } else if (!value) {
      setSelectedAsset(null);
    }
  }, [value, selectedAsset]);

  // 处理搜索
  const handleSearch = (value) => {
    setSearchValue(value);
    searchAssets(value);
  };

  // 处理选择
  const handleSelect = (value, option) => {
    const selectedAsset = assets.find(asset => asset.id === value);
    if (selectedAsset) {
      setSelectedAsset(selectedAsset);
    }
    if (onChange) {
      onChange(value, option);
    }
  };

  // 处理清除
  const handleClear = () => {
    setSelectedAsset(null);
    if (onChange) {
      onChange(undefined, undefined);
    }
  };

  // 获取显示文本
  const getDisplayText = (asset) => {
    return `${asset.name} (${asset.asset_no})`;
  };

  return (
    <Select
      value={value !== null ? value : undefined}
      onChange={handleSelect}
      onSearch={handleSearch}
      onClear={handleClear}
      placeholder={placeholder}
      disabled={disabled}
      style={style}
      showSearch
      allowClear
      filterOption={false}
      notFoundContent={loading ? <Spin size="small" /> : '暂无数据'}
      suffixIcon={<SearchOutlined />}
    >
      {assets.map(asset => (
        <Option key={asset.id} value={asset.id} asset={asset}>
          <div>
            <div style={{ fontWeight: 500 }}>{asset.name}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              编号: {asset.asset_no} | 状态: {asset.status}
            </div>
          </div>
        </Option>
      ))}
    </Select>
  );
};

export default AssetSearchSelect;
